/**
 * Turning audit rows into sentences.
 *
 * Pure, and tested, because this is the screen someone reads under pressure —
 * after a client asks who viewed their footage, or after something was deleted
 * that should not have been. A row that requires interpretation is a row that
 * gets misread at exactly the wrong moment.
 *
 * Every phrase names the actor, the act and the thing, in that order. No codes,
 * no ids where a name exists.
 */

import { relativeTime } from './deviceViewModel.ts';

export interface AuditRecord {
  id: string;
  actor_email: string | null;
  actor_kind: string;
  action: string;
  target_type: string | null;
  target_label: string | null;
  detail: Record<string, unknown> | null;
  source_ip: string | null;
  occurred_at: string;
}

/** Grouping, so the page can be read by concern rather than only by time. */
export type AuditCategory = 'auth' | 'footage' | 'credential' | 'access' | 'destructive' | 'other';

export interface AuditRowModel {
  id: string;
  category: AuditCategory;
  /** The whole event as one sentence. */
  summary: string;
  actor: string;
  whenLabel: string;
  sourceIp: string | null;
  /** True for anything that destroyed data — rendered so it stands out. */
  destructive: boolean;
}

const ACTOR_FALLBACK: Record<string, string> = {
  device: 'A device',
  system: 'The system',
};

function actorName(record: AuditRecord): string {
  if (record.actor_email) return record.actor_email;
  return ACTOR_FALLBACK[record.actor_kind] ?? 'Someone';
}

/** The thing acted on, preferring the snapshotted name over an id. */
function targetName(record: AuditRecord): string {
  if (record.target_label) return record.target_label;
  if (record.target_type) return `a ${record.target_type}`;
  return 'something';
}

function count(detail: Record<string, unknown> | null, key: string): number {
  const value = detail?.[key];
  return typeof value === 'number' ? value : 0;
}

/**
 * Describes what was destroyed, in the terms someone cares about.
 *
 * Days of history rather than a segment count: nobody knows what 26,000 segments
 * means, and everybody knows what eighteen days means.
 */
function lossPhrase(detail: Record<string, unknown> | null): string {
  const cameras = count(detail, 'cameras');
  const days = count(detail, 'historyDays');

  const parts: string[] = [];
  if (cameras > 0) parts.push(`${cameras} camera${cameras === 1 ? '' : 's'}`);
  if (days > 0) parts.push(`${days} day${days === 1 ? '' : 's'} of recording`);

  return parts.length > 0 ? `, destroying ${parts.join(' and ')}` : '';
}

export function auditRowModel(record: AuditRecord, now: number): AuditRowModel {
  const who = actorName(record);
  const what = targetName(record);
  const detail = record.detail;

  let summary: string;
  let category: AuditCategory = 'other';

  switch (record.action) {
    case 'auth.signed_in':
      category = 'auth';
      summary = `${who} signed in`;
      break;
    case 'auth.sign_in_failed':
      category = 'auth';
      // Named as an attempt, not a failure of the person — several in a row from
      // an unfamiliar address is the shape worth noticing.
      summary = `${who} — sign-in attempt rejected`;
      break;
    case 'auth.invite_accepted':
      category = 'auth';
      summary = `${who} accepted their invitation`;
      break;

    case 'footage.viewed_live':
      category = 'footage';
      summary = `${who} viewed live video from ${what}`;
      break;
    case 'footage.viewed_recording':
      category = 'footage';
      summary = `${who} viewed recorded footage from ${what}`;
      break;
    case 'footage.exported':
      category = 'footage';
      summary = `${who} exported footage from ${what}`;
      break;

    case 'credential.claim_code_issued':
      category = 'credential';
      summary = `${who} was shown a claim code for ${what}`;
      break;
    case 'credential.invite_issued':
      category = 'credential';
      summary = `${who} was shown an invitation link for ${what}`;
      break;
    case 'credential.camera_set':
      category = 'credential';
      summary = `${who} set the camera password on ${what}`;
      break;

    case 'access.user_invited':
      category = 'access';
      summary = `${who} invited ${what} as ${String(detail?.['role'] ?? 'a user')}`;
      break;
    case 'access.user_role_changed':
      category = 'access';
      summary = `${who} changed ${what} from ${String(detail?.['from'] ?? '?')} to ${String(detail?.['to'] ?? '?')}`;
      break;
    case 'access.user_access_changed': {
      category = 'access';
      const scope = String(detail?.['scope'] ?? '');
      summary =
        scope === 'all'
          ? `${who} gave ${what} access to every site`
          : `${who} limited ${what} to ${count(detail, 'sites')} site(s)`;
      break;
    }
    case 'access.user_removed':
      category = 'access';
      summary = `${who} removed the account ${what}`;
      break;

    case 'destructive.camera_deleted':
      category = 'destructive';
      summary = `${who} deleted the camera ${what}${lossPhrase(detail)}`;
      break;
    case 'destructive.device_deleted':
      category = 'destructive';
      summary = `${who} deleted the device ${what}${lossPhrase(detail)}`;
      break;
    case 'destructive.site_deleted':
      category = 'destructive';
      summary = `${who} deleted the site ${what}`;
      break;
    case 'destructive.client_deleted':
      category = 'destructive';
      summary = `${who} deleted the client ${what}`;
      break;
    case 'destructive.device_reprovisioned':
      category = 'destructive';
      summary = `${who} re-provisioned ${what}, cutting it off until it is claimed again`;
      break;
    case 'destructive.device_token_revoked':
      category = 'destructive';
      summary = `${who} revoked a device token for ${what}`;
      break;

    default:
      /*
       * An action this build has never heard of still renders. A log written by
       * a newer server and read by an older page must not show blank rows —
       * that is exactly when someone concludes nothing happened.
       */
      summary = `${who} — ${record.action}`;
  }

  const at = Date.parse(record.occurred_at);

  return {
    id: record.id,
    category,
    summary,
    actor: who,
    whenLabel: Number.isNaN(at) ? 'at an unknown time' : relativeTime(at, now),
    sourceIp: record.source_ip,
    destructive: category === 'destructive',
  };
}

export function auditViewModel(records: AuditRecord[], now: number): AuditRowModel[] {
  return records.map((record) => auditRowModel(record, now));
}

/**
 * How a deletion should be described before it happens.
 *
 * Returns null when there is nothing recorded to lose — in which case the plain
 * confirmation is honest and a dire warning would be theatre that trains people
 * to ignore the real one.
 */
export function describeLoss(impact: {
  cameras: number;
  historyDays: number;
  segments: number;
  devices: number;
}): string | null {
  if (impact.segments === 0) return null;

  const parts: string[] = [];
  if (impact.devices > 0) parts.push(`${impact.devices} device${impact.devices === 1 ? '' : 's'}`);
  if (impact.cameras > 0) parts.push(`${impact.cameras} camera${impact.cameras === 1 ? '' : 's'}`);

  const span =
    impact.historyDays > 0
      ? `${impact.historyDays} day${impact.historyDays === 1 ? '' : 's'} of recording`
      : 'recorded footage';

  return parts.length > 0
    ? `This destroys ${span} across ${parts.join(' and ')}.`
    : `This destroys ${span}.`;
}
